// O PDF dos relatos da família — o mesmo desenho dos outros documentos.
import { formatarData, formatarDataHora, formatarHora } from '@medicov1/shared';
import { TINTA, MEIO, FRACO, LINHA, ALTO, titulo, comTitulo, baixarPdfDe } from './documentoPdf';
import { notaNoPdf } from './notaNoPdf';
import { LEGENDA_DOS_RELATOS, rotuloDoDia, type RelatosDTO } from './relatorioRelatosTipos';

type Margem = [number, number, number, number];

/** Monta a definição do pdfmake. Pura: testável fora do navegador. */
export function montarDocumentoRelatos(dados: RelatosDTO): unknown {
  const p = dados.paciente;
  const conteudo: unknown[] = [];
  let n = 0;

  conteudo.push({
    columns: [
      {
        width: '*',
        stack: [
          { text: p.apelido, fontSize: 17, color: TINTA },
          { text: `${p.idade} anos${p.quarto ? ` · quarto ${p.quarto}` : ''} · internada desde ${formatarData(p.internadoDesde)}`, fontSize: 8, color: FRACO },
        ],
      },
      {
        width: 'auto',
        stack: [
          { text: 'RELATOS DA FAMÍLIA', fontSize: 8.6, bold: true, characterSpacing: 1.3, alignment: 'right', color: TINTA },
          { text: dados.gerado.periodoRotulo, fontSize: 7, color: FRACO, alignment: 'right', margin: [0, 2, 0, 0] as Margem },
          { text: `gerado em ${formatarDataHora(new Date(dados.gerado.em))}`, fontSize: 7, color: FRACO, alignment: 'right' },
        ],
      },
    ],
    margin: [0, 0, 0, 6] as Margem,
  });
  conteudo.push({ canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1.1, lineColor: TINTA }], margin: [0, 0, 0, 7] as Margem });
  conteudo.push({ text: LEGENDA_DOS_RELATOS, fontSize: 7.8, color: MEIO, lineHeight: 1.3, margin: [0, 0, 0, 4] as Margem });
  conteudo.push({
    text: dados.total === 0
      ? 'Nenhum relato neste período.'
      : `${dados.total} ${dados.total === 1 ? 'relato' : 'relatos'} em ${dados.dias.length} ${dados.dias.length === 1 ? 'dia' : 'dias'}`
        + `${dados.autores.length > 0 ? `, escritos por ${dados.autores.join(', ')}` : ''}.`,
    fontSize: 7.4, color: FRACO, margin: [0, 0, 0, 2] as Margem,
  });

  // `continua`: a segunda parte do dia, que precisa da linha de cima para
  // separar do primeiro relato.
  const tabelaDe = (relatos: RelatosDTO['dias'][number]['relatos'], continua: boolean) => ({
      margin: [0, continua ? 0 : 3, 0, 0] as Margem,
      table: {
        headerRows: 0, dontBreakRows: true, widths: [40, '*'],
        body: relatos.map((r) => [
          {
            stack: [
              { text: formatarHora(new Date(r.ocorreuEm)), fontSize: 8, color: TINTA, bold: true },
              { text: r.autor, fontSize: 6.6, color: FRACO },
            ],
          },
          notaNoPdf(r.texto, 8.4, { texto: TINTA, medio: MEIO, linha: LINHA, alerta: ALTO }),
        ]),
      },
      layout: {
        hLineWidth: (i: number, node: { table: { body: unknown[] } }) =>
          ((i === 0 && !continua) || i === node.table.body.length ? 0 : 0.4),
        vLineWidth: () => 0, hLineColor: () => LINHA,
        paddingLeft: () => 0, paddingRight: () => 8, paddingTop: () => 5, paddingBottom: () => 5,
      },
  });

  for (const d of dados.dias) {
    // O título vai grudado SÓ no primeiro relato: um dia com vinte relatos
    // passa de uma página, e bloco inquebrável maior que a folha o pdfmake
    // corta. O resto do dia quebra entre relatos, nunca no meio de um.
    conteudo.push(...comTitulo(
      titulo(++n, rotuloDoDia(d.dia), d.relatos.length === 1 ? 'um relato' : `${d.relatos.length} relatos`),
      tabelaDe(d.relatos.slice(0, 1), false),
      d.relatos.length > 1 ? [tabelaDe(d.relatos.slice(1), true)] : [],
    ));
  }

  return {
    pageSize: 'A4',
    pageMargins: [36, 34, 36, 38],
    defaultStyle: { font: 'Roboto', fontSize: 8, color: TINTA },
    info: { title: 'Relatos da família', author: 'saudev1' },
    footer: (pagina: number, total: number) => ({
      columns: [
        { text: 'saudev1 · relatos da família, do jeito que foram escritos · não é prontuário nem diagnóstico', fontSize: 6.2, color: FRACO },
        { text: `${pagina} / ${total}`, fontSize: 6.2, color: FRACO, alignment: 'right' },
      ],
      margin: [36, 0, 36, 0] as Margem,
    }),
    content: conteudo,
  };
}

export async function gerarPdfRelatos(dados: RelatosDTO): Promise<void> {
  const data = formatarData(new Date(dados.gerado.em)).replace(/\//g, '-');
  await baixarPdfDe(montarDocumentoRelatos(dados), `relatos-${dados.gerado.periodo}-${data}.pdf`);
}
